import {
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  SourcingEventV2Dto,
  useListPublicBidsForEventQuery,
} from "../../../autogen/bff-api";
import { AwardTab } from "./award/AwardTab";
import { BidsTab } from "./BidsTab";
import { CommunicationTab } from "./communication/CommunicationTab";
import { EvaluationTab } from "./evaluation/EvaluationTab";
import { RequestTab } from "./RequestTab";
import { buildSupplierRows, isBidDeadlinePassed } from "./supplierRows";
import { useEventTabLabel } from "./useEventTabLabel";

export type EventTab =
  | "request"
  | "bids"
  | "evaluation"
  | "award"
  | "communication"
  | "tasks"
  | "team";

const TABS: { key: EventTab; labelKey: string }[] = [
  { key: "request", labelKey: "Request" },
  { key: "bids", labelKey: "Bids" },
  { key: "evaluation", labelKey: "Evaluation" },
  { key: "award", labelKey: "Award" },
  { key: "communication", labelKey: "Communication" },
  { key: "tasks", labelKey: "Tasks" },
  { key: "team", labelKey: "Team" },
];

export const DEFAULT_TAB: EventTab = "request";

export const parseEventTab = (value: string | null): EventTab => {
  const found = TABS.find((tab) => tab.key === value);
  return found ? found.key : DEFAULT_TAB;
};

const TabPlaceholder = ({ label }: { label: string }) => {
  const { t } = useTranslation();
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap="2"
      py="16"
      rounded="xl"
      bg="smBackground"
      border="1px dashed"
      borderColor="smBorder"
      color="smMuted"
    >
      <Text fontWeight="medium">{label}</Text>
      <Text fontSize="sm">{t("Coming soon")}</Text>
    </Flex>
  );
};

interface Props {
  event: SourcingEventV2Dto;
  activeTab: EventTab;
  onTabChange: (tab: EventTab) => void;
}

export const EventTabs = ({ event, activeTab, onTabChange }: Props) => {
  const tabLabel = useEventTabLabel();
  const { data: bidsData } = useListPublicBidsForEventQuery({
    eventId: event.id,
  });
  // Count the suppliers shown in the Tilbud tab: registrants before the deadline, registrants plus
  // anyone who delivered after it. Never the raw bid count, which would leak submissions early.
  const revealedBids = isBidDeadlinePassed(event) ? bidsData?.bids ?? [] : [];
  const supplierCount = buildSupplierRows(event, revealedBids).length;
  const index = Math.max(
    0,
    TABS.findIndex((tab) => tab.key === activeTab),
  );

  return (
    <Tabs
      index={index}
      onChange={(i) => onTabChange(TABS[i].key)}
      colorScheme="teal"
      isLazy
    >
      <TabList overflowX="auto" overflowY="hidden">
        {TABS.map((tab) => (
          <Tab key={tab.key} whiteSpace="nowrap">
            {tabLabel(tab.labelKey)}
            {tab.key === "bids" && supplierCount > 0 ? ` (${supplierCount})` : ""}
          </Tab>
        ))}
      </TabList>
      <TabPanels>
        <TabPanel px="0">
          <RequestTab
            eventData={event.eventData}
            publicContext={event.publicContext}
          />
        </TabPanel>
        {TABS.slice(1).map((tab) => (
          <TabPanel key={tab.key} px="0">
            {tab.key === "bids" ? (
              <BidsTab event={event} />
            ) : tab.key === "evaluation" ? (
              <EvaluationTab event={event} />
            ) : tab.key === "award" ? (
              <AwardTab event={event} />
            ) : tab.key === "communication" ? (
              <CommunicationTab eventId={event.id} />
            ) : (
              <TabPlaceholder label={tabLabel(tab.labelKey)} />
            )}
          </TabPanel>
        ))}
      </TabPanels>
    </Tabs>
  );
};
